import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Calendar, MapPin, ChevronRight, RefreshCw,
  CheckCircle, AlertCircle, Car
} from 'lucide-react';

export default function DriverEvents() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/driver/my-event-assignments`);
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to load event assignments');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'assigned':
        return <span className="px-2 py-1 text-xs rounded-full bg-gold-100 text-gold-700">Assigned</span>;
      case 'checked_in':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Checked In</span>;
      case 'active':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Active</span>;
      case 'checked_out':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Completed</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  const upcomingEvents = assignments.filter(a => {
    const eventDate = new Date(a.event?.start_date);
    return eventDate >= new Date() && a.status !== 'checked_out';
  });

  const activeEvents = assignments.filter(a => 
    a.status === 'checked_in' || a.status === 'active'
  );

  const completedEvents = assignments.filter(a => a.status === 'checked_out');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Event Assignments</h1>
          <p className="text-gray-500">Track and manage your event assignments</p>
        </div>
        <Button variant="outline" onClick={fetchAssignments}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Active Events - Priority Display */}
      {activeEvents.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-green-600">
            <Car className="w-5 h-5" />
            Active Now
          </h2>
          {activeEvents.map((assignment) => (
            <Card 
              key={assignment.id} 
              className="border-green-200 bg-green-50 dark:bg-green-900/20 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/driver/events/${assignment.event_id}/tracking`)}
              data-testid={`active-event-${assignment.event_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{assignment.event?.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {assignment.assigned_location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Car className="w-4 h-4" />
                        {assignment.total_trips || 0} trips
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(assignment.status)}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Upcoming Events
          </h2>
          {upcomingEvents.map((assignment) => (
            <Card 
              key={assignment.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/driver/events/${assignment.event_id}/tracking`)}
              data-testid={`upcoming-event-${assignment.event_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold">{assignment.event?.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(assignment.event?.start_date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {assignment.assigned_location}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{assignment.assigned_location_address}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(assignment.status)}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Completed Events */}
      {completedEvents.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-500">
            <CheckCircle className="w-5 h-5" />
            Completed Events
          </h2>
          {completedEvents.map((assignment) => (
            <Card 
              key={assignment.id} 
              className="bg-gray-50 dark:bg-gray-800 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/driver/events/${assignment.event_id}/tracking`)}
              data-testid={`completed-event-${assignment.event_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-600">{assignment.event?.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(assignment.event?.start_date).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Car className="w-4 h-4" />
                        {assignment.total_trips || 0} trips completed
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(assignment.status)}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {assignments.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No Event Assignments</h3>
            <p className="text-gray-400 mt-1">You haven't been assigned to any events yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
